import networkService from "services/network/network.service"
import type {
  TemplateDto,
  TemplateImageSlot,
} from "@screens/templates/services/templates-api.service"

export interface AdminTemplateRow {
  id: string
  mediaType: "video" | "image"
  name: string
  description: string | null
  prompt: string
  modelKey: string
  aspectRatio: string
  category: string
  contentType: string
  language: string
  imageSlots: TemplateImageSlot[]
  previewMediaUrl: string | null
  previewPosterUrl: string | null
  previewDurationSec: number | null
  modelDisplayLabel: string | null
  /** Optional admin-curated reference image used by `Recreate` for new users. */
  sourceImageUrl: string | null
  isActive: boolean
  displayOrder: number
  partnerId: number | null
  previewJobId: string | null
  createdAt: string
  updatedAt: string
}

export interface PaginatedAdminTemplates {
  data: AdminTemplateRow[]
  total: number
  totalPages: number
}

const adminBase = "/admin/templates"

export async function adminListTemplates(params: {
  page?: number
  limit?: number
  search?: string
  mediaType?: "video" | "image"
  partnerId?: number
  isActive?: boolean
}): Promise<PaginatedAdminTemplates> {
  const sp = new URLSearchParams()
  if (params.page) sp.set("page", String(params.page))
  if (params.limit) sp.set("limit", String(params.limit))
  if (params.search) sp.set("search", params.search)
  if (params.mediaType) sp.set("mediaType", params.mediaType)
  if (params.partnerId != null) sp.set("partnerId", String(params.partnerId))
  if (params.isActive !== undefined) {
    sp.set("isActive", String(params.isActive))
  }
  const { data } = await networkService.axios.get<PaginatedAdminTemplates>(
    `${adminBase}?${sp.toString()}`
  )
  return data
}

export async function adminBatchCreateTemplates(body: {
  items: Array<{
    mediaType: "video" | "image"
    name: string
    description?: string
    prompt: string
    modelKey: string
    aspectRatio: "9:16" | "16:9" | "1:1"
    category: string
    contentType: "hook" | "story" | "cta" | "full-ad"
    language: string
    imageSlots: TemplateImageSlot[]
    previewMediaUrl?: string
    previewPosterUrl?: string
    previewDurationSec?: number
    modelDisplayLabel?: string
    sourceImageUrl?: string
    isActive?: boolean
    displayOrder?: number
    partnerId?: number
  }>
  generatePreview?: boolean
}): Promise<{
  created: AdminTemplateRow[]
  previewJobIds: Array<{
    templateId: string
    jobId?: string
    error?: string
  }>
}> {
  const { data } = await networkService.axios.post(`${adminBase}/batch`, body)
  return data
}

export async function adminCreateTemplate(body: {
  mediaType: "video" | "image"
  name: string
  description?: string
  prompt: string
  modelKey: string
  aspectRatio: "9:16" | "16:9" | "1:1"
  category: string
  contentType: "hook" | "story" | "cta" | "full-ad"
  language: string
  imageSlots: TemplateImageSlot[]
  previewMediaUrl?: string
  previewPosterUrl?: string
  previewDurationSec?: number
  modelDisplayLabel?: string
  sourceImageUrl?: string
  isActive?: boolean
  displayOrder?: number
  partnerId?: number
}): Promise<AdminTemplateRow> {
  const { data } = await networkService.axios.post(adminBase, body)
  return data
}

export async function adminUpdateTemplate(
  id: string,
  body: Partial<{
    name: string
    description: string
    prompt: string
    modelKey: string
    aspectRatio: "9:16" | "16:9" | "1:1"
    category: string
    contentType: "hook" | "story" | "cta" | "full-ad"
    language: string
    imageSlots: TemplateImageSlot[]
    previewMediaUrl: string
    previewPosterUrl: string
    previewDurationSec: number
    modelDisplayLabel: string
    sourceImageUrl: string | null
    isActive: boolean
    displayOrder: number
    partnerId: number | null
  }>
): Promise<AdminTemplateRow> {
  const { data } = await networkService.axios.put(`${adminBase}/${id}`, body)
  return data
}

export async function adminSetTemplateActive(
  id: string,
  isActive: boolean
): Promise<AdminTemplateRow> {
  const { data } = await networkService.axios.patch(
    `${adminBase}/${id}/active`,
    { isActive }
  )
  return data
}

export async function adminGenerateTemplatePreview(id: string): Promise<{
  jobId?: string
  jobIds?: string[]
  alreadyRunning?: boolean
  syncedFromExistingJob?: boolean
}> {
  const { data } = await networkService.axios.post(
    `${adminBase}/${id}/generate-preview`
  )
  return data
}

export async function adminClearTemplatePreviewJob(
  id: string
): Promise<AdminTemplateRow> {
  const { data } = await networkService.axios.patch(
    `${adminBase}/${id}/clear-preview-job`
  )
  return data
}

export async function adminDeleteTemplate(id: string): Promise<void> {
  await networkService.axios.delete(`${adminBase}/${id}`)
}

// ---------------------------------------------------------------------------
// "Save as Template" — promote gallery asset to templates library
// ---------------------------------------------------------------------------

export type TemplateVideoPart = "full_ad" | "hook" | "body" | "cta"

export interface TemplateVoiceMetadata {
  voiceId?: string
  voiceName?: string
  transcript?: string
}

export interface FromGalleryPrefill {
  suggestedTitle: string
  cleanedPrompt: string
  mediaType: "video" | "image"
  videoPart: TemplateVideoPart
  model: string
  modelDisplayLabel: string
  aspectRatio: "9:16" | "16:9" | "1:1"
  style: string
  language: string
  voice?: TemplateVoiceMetadata
  isComposed: boolean
  completed: boolean
  previewUrl: string | null
  posterUrl: string | null
  existingTemplateId: string | null
}

/** Category options (distinct categories already in use + defaults). */
export async function adminGetTemplateCategories(): Promise<string[]> {
  const { data } = await networkService.axios.get<{ categories: string[] }>(
    `${adminBase}/categories`
  )
  return data.categories
}

/**
 * Server-side prefill for the Save as Template modal — the backend owns the
 * prompt-cleaning + title rules (single unit-tested implementation).
 */
export async function adminGetFromGalleryPrefill(params: {
  galleryId?: string
  mediaId?: number
}): Promise<FromGalleryPrefill> {
  const sp = new URLSearchParams()
  if (params.galleryId) sp.set("galleryId", params.galleryId)
  if (params.mediaId != null) sp.set("mediaId", String(params.mediaId))
  const { data } = await networkService.axios.get<FromGalleryPrefill>(
    `${adminBase}/from-gallery/prefill?${sp.toString()}`
  )
  return data
}

/**
 * Promote a gallery asset to the templates library. A 409 means the asset is
 * already a template; the response body carries the existing `templateId`.
 */
export async function adminCreateTemplateFromGallery(body: {
  galleryId?: string
  mediaId?: number
  title: string
  prompt: string
  categoryId: string
  videoPart?: TemplateVideoPart
  overrides?: {
    model?: string
    aspectRatio?: "9:16" | "16:9" | "1:1"
    style?: string
    language?: string
    voice?: TemplateVoiceMetadata
  }
}): Promise<{ templateId: string; previewUrl: string | null }> {
  const { data } = await networkService.axios.post(
    `${adminBase}/from-gallery`,
    body
  )
  return data
}

export interface ParsedTemplateItem {
  title: string
  prompt: string
  description: string
}

/**
 * Server-side AI parser (gpt-4o-mini) for admin batch creation. Used as a
 * fallback when our local markdown parser can't extract structure from the
 * pasted document (table layouts, plain prose, ChatGPT Canvas exports, ...).
 */
export async function adminParseTemplatesFromText(body: {
  text: string
  mediaType?: "video" | "image"
}): Promise<{ items: ParsedTemplateItem[] }> {
  const { data } = await networkService.axios.post<{
    items: ParsedTemplateItem[]
  }>(`${adminBase}/parse-text`, body)
  return data
}

export interface PartnerRow {
  id: number
  name: string
  slug: string
  contactEmail: string | null
  isActive: boolean
  notes: string | null
  createdAt: string
  updatedAt: string
  // Affiliate program fields
  type?: string
  status?: "pending" | "approved" | "suspended"
  ownerUserId?: string | null
  commissionPct?: number
  promptsActive?: boolean
  stripeConnectStatus?: string
}

const partnersBase = "/admin/partners"

export async function adminListPartners(params: {
  page?: number
  limit?: number
  search?: string
}): Promise<{ data: PartnerRow[]; total: number; totalPages: number }> {
  const sp = new URLSearchParams()
  if (params.page) sp.set("page", String(params.page))
  if (params.limit) sp.set("limit", String(params.limit))
  if (params.search) sp.set("search", params.search)
  const { data } = await networkService.axios.get(
    `${partnersBase}?${sp.toString()}`
  )
  return data
}

export async function adminCreatePartner(body: {
  name: string
  slug: string
  contactEmail?: string
  isActive?: boolean
  notes?: string
}): Promise<PartnerRow> {
  const { data } = await networkService.axios.post(partnersBase, body)
  return data
}

export async function adminUpdatePartner(
  id: number,
  body: Partial<{
    name: string
    slug: string
    contactEmail: string
    isActive: boolean
    notes: string
    status: "pending" | "approved" | "suspended"
    commissionPct: number
    promptsActive: boolean
  }>
): Promise<PartnerRow> {
  const { data } = await networkService.axios.put(`${partnersBase}/${id}`, body)
  return data
}

/** Approve a pending affiliate (enables their dashboard/payouts). */
export async function adminApprovePartner(id: number): Promise<PartnerRow> {
  const { data } = await networkService.axios.post(
    `${partnersBase}/${id}/approve`
  )
  return data
}

/** Suspend an affiliate. */
export async function adminSuspendPartner(id: number): Promise<PartnerRow> {
  const { data } = await networkService.axios.post(
    `${partnersBase}/${id}/suspend`
  )
  return data
}

export async function adminListPartnerTemplates(
  partnerId: number
): Promise<TemplateDto[]> {
  const { data } = await networkService.axios.get(
    `${partnersBase}/${partnerId}/templates`
  )
  return data
}

// ---------------------------------------------------------------------------
// Curated launch set — one-click seeding of the session-log style templates
// ---------------------------------------------------------------------------

export interface SeedCuratedItem {
  n: number
  title: string
  galleryId: string
  status: "created" | "already-exists" | "failed" | "would-create"
  templateId?: string
  promptChars?: number
  caveat?: string
  error?: string
}

export interface SeedCuratedReport {
  dryRun: boolean
  created: number
  alreadyExists: number
  failed: number
  items: SeedCuratedItem[]
}

/**
 * Promote the curated set into the templates library. Runs server-side against
 * whichever environment the API is, so there is no host/token to get wrong.
 * Idempotent — re-running reports already-promoted assets as "already-exists".
 */
export async function adminSeedCuratedTemplates(
  dryRun: boolean,
): Promise<SeedCuratedReport> {
  const { data } = await networkService.axios.post<SeedCuratedReport>(
    `${adminBase}/seed-curated${dryRun ? "?dryRun=true" : ""}`,
  )
  return data
}
